// src/services/apiClient.js
const getApiBaseUrl = () => {
    return import.meta.env.VITE_API_URL || '';
};

const API_BASE_URL = getApiBaseUrl();

export class ApiClient {
    constructor() {
        this.baseURL = API_BASE_URL;
    }

    // Create default headers
    getDefaultHeaders() {
        return {
            'Content-Type': 'application/json',
        };
    }

    // The visible URL is the only browser-side source for tenant context. Replace
    // any caller-supplied location/user values so a component cannot silently make
    // a request for a tenant other than the one shown in the address bar.
    addUrlParams(path) {
        const currentParams = new URLSearchParams(window.location.search);
        const hashIndex = path.indexOf('#');
        const hash = hashIndex >= 0 ? path.slice(hashIndex) : '';
        const pathWithoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
        const queryIndex = pathWithoutHash.indexOf('?');
        const pathname = queryIndex >= 0 ? pathWithoutHash.slice(0, queryIndex) : pathWithoutHash;
        const pathParams = new URLSearchParams(
            queryIndex >= 0 ? pathWithoutHash.slice(queryIndex + 1) : ''
        );

        ['location', 'user'].forEach((key) => {
            const value = currentParams.get(key);
            if (value) {
                pathParams.set(key, value);
            } else {
                pathParams.delete(key);
            }
        });

        const integration = currentParams.get('integration');
        if (integration) {
            pathParams.set('integration', integration);
        }

        const query = pathParams.toString();
        return `${pathname}${query ? `?${query}` : ''}${hash}`;
    }

    resolveUrl(endpoint) {
        return `${this.baseURL.replace(/\/$/, '')}${endpoint}`;
    }

    // Generic fetch wrapper
    async request(endpoint, options = {}) {
        // Every authenticated API call carries the same visible location/user
        // routing context as the current page. The HttpOnly cookie remains the
        // authority and the backend requires an exact match.
        const contextualEndpoint = this.addUrlParams(endpoint);
        const url = this.resolveUrl(contextualEndpoint);

        const config = {
            headers: this.getDefaultHeaders(),
            credentials: 'include',
            ...options,
        };

        // Merge headers if custom headers are provided
        if (options.headers) {
            config.headers = {
                ...config.headers,
                ...options.headers,
            };
        }

        try {
            const response = await fetch(url, config);

            // Handle 401 - authentication failed
            if (response.status === 401) {
                const error = new Error('Authentication expired. Please refresh the page.');
                error.status = response.status;
                throw error;
            }

            // Handle other HTTP errors
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const error = new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
                error.status = response.status;
                throw error;
            }

            return await response.json();
        } catch (error) {
            // Don't log 403 errors - these are expected for permission checks
            if (error.status !== 403) {
                console.error(`API Error [${contextualEndpoint}]:`, error);
            }
            throw error;
        }
    }

    // HTTP Methods
    async get(endpoint, options = {}) {
        return this.request(endpoint, {
            method: 'GET',
            ...options,
        });
    }

    async post(endpoint, data = null, options = {}) {
        return this.request(endpoint, {
            method: 'POST',
            body: data ? JSON.stringify(data) : null,
            ...options,
        });
    }

    async put(endpoint, data = null, options = {}) {
        return this.request(endpoint, {
            method: 'PUT',
            body: data ? JSON.stringify(data) : null,
            ...options,
        });
    }

    async delete(endpoint, options = {}) {
        return this.request(endpoint, {
            method: 'DELETE',
            ...options,
        });
    }

    async patch(endpoint, data = null, options = {}) {
        return this.request(endpoint, {
            method: 'PATCH',
            body: data ? JSON.stringify(data) : null,
            ...options,
        });
    }
}

// Create a singleton instance
const apiClient = new ApiClient();

// Export specific API functions - all use Ejento endpoints
export const authAPI = {
    getMe: () => apiClient.get(apiClient.addUrlParams('/api/auth/verify-ejento')),
    logout: () => apiClient.post(apiClient.addUrlParams('/api/auth/logout')),
    getGoogleStartUrl: (location, user = null, portalFlow = null) => {
        const params = new URLSearchParams({ location });
        if (user) params.set('user', user);
        if (portalFlow) params.set('portalFlow', portalFlow);
        return apiClient.resolveUrl(`/api/auth/google/start?${params.toString()}`);
    },
    getPortalLoginStatus: (flowId, options = {}) =>
        apiClient.post('/api/auth/google/portal/status', { flowId }, options),
    acknowledgePortalLogin: (flowId, options = {}) =>
        apiClient.post('/api/auth/google/portal/ack', { flowId }, options),
    verifyPortalSession: async ({ flowId, locationId, userId }, options = {}) => {
        // An unscoped login may learn its user only after Google completes.
        // This probe is bound to that completed flow on the server, and never
        // changes the visible routing context or clears a cookie on failure.
        const params = new URLSearchParams({ location: locationId, user: userId });
        const response = await fetch(
            apiClient.resolveUrl(`/api/auth/google/portal/verify?${params}`),
            {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ flowId }),
                cache: 'no-store',
                ...options
            }
        );
        return response.ok && (await response.json()).success === true;
    },
    requestGoogleAccess: () =>
        apiClient.post(apiClient.addUrlParams('/api/auth/google/access-request')),
};

export const dashboardAPI = {
    getStats: () => apiClient.get(apiClient.addUrlParams('/api/ejento/dashboard/stats')),
    getTopTechnicians: () => apiClient.get(apiClient.addUrlParams('/api/ejento/dashboard/top-technicians')),
    getRewardSettings: () => apiClient.get(apiClient.addUrlParams('/api/payments/reward-settings')),
    updateRewardSettings: (settings) => apiClient.post(apiClient.addUrlParams('/api/payments/reward-settings'), settings),
    getLeaderboardSettings: () => apiClient.get(apiClient.addUrlParams('/api/ejento/settings/rewards')),
    updateLeaderboardSettings: (settings) => apiClient.put(apiClient.addUrlParams('/api/ejento/settings/rewards'), settings),
};

export const reviewsAPI = {
    getReviews: (params = {}) => {
        const searchParams = new URLSearchParams();
        
        // Add pagination parameters - ensure page is a number
        if (params.page !== undefined && params.page !== null) {
            searchParams.append('page', String(params.page));
        }
        if (params.limit !== undefined && params.limit !== null) {
            searchParams.append('limit', String(params.limit));
        }
        
        // Add filter parameters
        if (params.search) searchParams.append('search', params.search);
        if (params.rating) searchParams.append('rating', params.rating);
        if (params.assignmentStatus) searchParams.append('assignmentStatus', params.assignmentStatus);
        if (params.processingStatus) searchParams.append('processingStatus', params.processingStatus);
        
        // Add sorting parameters
        if (params.sortBy) searchParams.append('sortBy', params.sortBy);
        if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);
        
        const queryString = searchParams.toString();
        const url = `/api/google/reviews${queryString ? `?${queryString}` : ''}`;
        
        return apiClient.get(url);
    },

    generateResponse: (reviewId) =>
        apiClient.post(apiClient.addUrlParams(`/api/google/reviews/${reviewId}/generate-response`)),

    approveResponse: (reviewId) =>
        apiClient.post(apiClient.addUrlParams(`/api/google/reviews/${reviewId}/approve-response`)),

    publishResponse: (reviewId) =>
        apiClient.post(apiClient.addUrlParams(`/api/google/reviews/${reviewId}/publish-response`)),

    getPublishedReviews: () =>
        apiClient.get(apiClient.addUrlParams('/api/google/reviews?status=published')),

    assignReview: (reviewId, technicianIdOrCrmCode) => {
        // Always treat as crmCode since that's what we're passing from the technician selection
        const body = { crmCode: technicianIdOrCrmCode };
        return apiClient.post(apiClient.addUrlParams(`/api/google/reviews/${reviewId}/assign`), body);
    },
};

export const techniciansAPI = {
    getTechnicians: () => apiClient.get('/api/technicians'),

    getTechnicianByCrmCode: (crmCode) =>
        apiClient.get(`/api/technicians/crm/${crmCode}`),

    updatePersona: (crmCode, persona) =>
        apiClient.put(`/api/technicians/crm/${crmCode}/persona`, { persona }),
};

export const promptsAPI = {
    getPrompts: (type = null) =>
        apiClient.get(`/api/ejento/prompts${type ? `?type=${type}` : ''}`),

    getActivePrompt: (type) =>
        apiClient.get(`/api/ejento/prompts/active/${type}`),

    createPrompt: (promptData) =>
        apiClient.post('/api/ejento/prompts', promptData),

    updatePrompt: (id, promptData) =>
        apiClient.put(`/api/ejento/prompts/${id}`, promptData),

    activatePrompt: (id, data = {}) =>
        apiClient.post(`/api/ejento/prompts/${id}/activate`, data),
};

export const testingAPI = {
    generateResponse: (reviewData, technicianData, responsePrompt, useCustomPrompts) =>
        apiClient.post('/api/ejento/testing/generate-response', {
            reviewData,
            technicianData,
            responsePrompt,
            useCustomPrompts,
        }),

    getSampleReviews: () =>
        apiClient.get('/api/ejento/testing/sample-reviews'),
};

// Admin API (for Ejento management)
export const adminAPI = {
    getInvitations: () =>
        apiClient.get(apiClient.addUrlParams('/api/admin/auth/invitations')),

    createInvitation: ({ email, role }) =>
        apiClient.post(apiClient.addUrlParams('/api/admin/auth/invitations'), { email, role }),

    resendInvitation: (invitationId) =>
        apiClient.post(apiClient.addUrlParams(`/api/admin/auth/invitations/${encodeURIComponent(invitationId)}/resend`)),

    deleteInvitation: (invitationId) =>
        apiClient.delete(apiClient.addUrlParams(`/api/admin/auth/invitations/${encodeURIComponent(invitationId)}`)),

    getAccessRequests: () =>
        apiClient.get(apiClient.addUrlParams('/api/admin/auth/access-requests')),

    approveAccessRequest: (requestId, role) =>
        apiClient.post(
            apiClient.addUrlParams(`/api/admin/auth/access-requests/${encodeURIComponent(requestId)}/approve`),
            { role }
        ),

    denyAccessRequest: (requestId) =>
        apiClient.post(apiClient.addUrlParams(`/api/admin/auth/access-requests/${encodeURIComponent(requestId)}/deny`)),

    getLocationMappings: () =>
        apiClient.get(apiClient.addUrlParams('/api/admin/ejento/locations')),

    createLocationMapping: (locationData) =>
        apiClient.post(apiClient.addUrlParams('/api/admin/ejento/locations'), locationData),

    getUserMappings: (locationId, page = 1, limit = 10, search = '', role = 'all') => {
        const basePath = '/api/admin/ejento/users';
        const params = new URLSearchParams();
        if (locationId) params.append('locationId', locationId);
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        if (search) params.append('search', search);
        if (role !== 'all') params.append('role', role);
        const query = params.toString() ? `?${params.toString()}` : '';
        return apiClient.get(apiClient.addUrlParams(`${basePath}${query}`));
    },

    createUserMapping: (userData) =>
        apiClient.post(apiClient.addUrlParams('/api/admin/ejento/users'), userData),

    updateUserMapping: (userId, userData) =>
        apiClient.put(apiClient.addUrlParams(`/api/admin/ejento/users/${userId}`), userData),

    deleteUserMapping: (userId) =>
        apiClient.delete(apiClient.addUrlParams(`/api/admin/ejento/users/${userId}`)),

    getUnmappedTechnicians: () =>
        apiClient.get(apiClient.addUrlParams('/api/admin/ejento/unmapped-technicians')),

    createTechnicianMapping: (technicianId, userRole = 'technician') =>
        apiClient.post(apiClient.addUrlParams(`/api/admin/ejento/create-mapping/${technicianId}`), { userRole }),

    getCrmSettings: () =>
        apiClient.get(apiClient.addUrlParams('/api/admin/crm/settings')),

    updateCrmSettings: (type, items) =>
        apiClient.post(apiClient.addUrlParams(`/api/admin/crm/settings/${type}`), { items }),

    generateTestUrl: (locationId, userId) =>
        apiClient.post(apiClient.addUrlParams('/api/admin/ejento/generate-url'), { locationId, userId }),

    // Integration endpoints
    getIntegration: (type) =>
        apiClient.get(apiClient.addUrlParams(`/api/admin/integration/${type}`)),

    saveIntegration: (type, credentials) =>
        apiClient.post(apiClient.addUrlParams(`/api/admin/integration/${type}`), { credentials }),

    testIntegration: (type) =>
        apiClient.post(apiClient.addUrlParams(`/api/admin/integration/${type}/test`)),

    getProviderWebhookConfiguration: () =>
        apiClient.get(apiClient.addUrlParams('/api/admin/provider-webhooks')),

    saveProviderWebhookConfiguration: (configuration) =>
        apiClient.post(apiClient.addUrlParams('/api/admin/provider-webhooks'), configuration),

    syncTechnicians: (type) =>
        apiClient.post(apiClient.addUrlParams(`/api/admin/integration/${type}/sync/technicians`)),
    
    syncUnifiedTechnicians: () =>
        apiClient.post(apiClient.addUrlParams('/api/admin/sync/unified-technicians'), {}),
    
    debugGhlIntegration: () =>
        apiClient.get(apiClient.addUrlParams('/api/admin/debug/ghl-integration')),
    
    setupGhlIntegration: (locationId, accessToken) =>
        apiClient.post(apiClient.addUrlParams('/api/admin/setup/ghl-integration'), { locationId, accessToken }),
    
    fixGhlIntegrationClient: () =>
        apiClient.post(apiClient.addUrlParams('/api/admin/fix/ghl-integration-client'), {}),
    
    setupPestPacCredentials: (credentials) =>
        apiClient.post(apiClient.addUrlParams('/api/admin/setup/pestpac-credentials'), credentials),

    getWebhookStatus: (type) =>
        apiClient.get(apiClient.addUrlParams(`/api/admin/integration/${type}/webhooks/status`)),

    // Appointment management (replaces campaign management)
    getAppointments: (filters = {}) =>
        apiClient.get(apiClient.addUrlParams('/api/admin/appointments', filters)),

    syncAppointments: (dateRange = {}) =>
        apiClient.post('/api/admin/appointments/sync', dateRange),

    testWebhook: (data) =>
        apiClient.post('/api/webhooks/pestpac/test', data),

    updateTechnicianRoles: () =>
        apiClient.post(apiClient.addUrlParams('/api/admin/update-technician-roles')),

    // PestPac selective sync
    getPestPacUsers: () =>
        apiClient.get(apiClient.addUrlParams('/api/admin/pestpac/users')),

    syncSelectedUsers: (selectedUserIds, syncOptions = {}) =>
        apiClient.post(apiClient.addUrlParams('/api/admin/pestpac/sync-selected-users'), {
            selectedUserIds,
            syncOptions
        }),

    unsyncSelectedUsers: (selectedUserIds) =>
        apiClient.post(apiClient.addUrlParams('/api/admin/pestpac/unsync-selected-users'), {
            selectedUserIds
        }),

    // FieldRoutes selective sync
    getFieldRoutesEmployees: () =>
        apiClient.get(apiClient.addUrlParams('/api/admin/fieldroutes/employees')),

    syncFieldRoutesUsers: (selectedUserIds, syncOptions = {}) =>
        apiClient.post(apiClient.addUrlParams('/api/admin/fieldroutes/sync-selected-users'), {
            selectedUserIds,
            syncOptions
        }),

    // Review auto-match service
    triggerAutoMatch: () =>
        apiClient.post(apiClient.addUrlParams('/api/admin/review-auto-match/trigger')),

    getAutoMatchStatus: () =>
        apiClient.get(apiClient.addUrlParams('/api/admin/review-auto-match/status')),

    // Review solicitation settings and stats
    getReviewSolicitationSettings: () =>
        apiClient.get(apiClient.addUrlParams('/api/admin/review-solicitation/settings')),

    updateReviewSolicitationSettings: (settings) =>
        apiClient.put(apiClient.addUrlParams('/api/admin/review-solicitation/settings'), settings),

    getReviewSolicitationTestAppointments: (params = {}) =>
        apiClient.get(apiClient.addUrlParams('/api/admin/review-solicitation/test-appointments', params)),

    sendReviewSolicitationTest: (payload) =>
        apiClient.post(apiClient.addUrlParams('/api/admin/review-solicitation/test-send'), payload),

    getCampaignStats: (params = {}) =>
        apiClient.get(apiClient.addUrlParams('/api/admin/campaigns/stats', params))
};

// Google My Business API
export const googleAPI = {
    startOAuth: () =>
        apiClient.get(apiClient.addUrlParams('/api/google/oauth/start')),

    testConnection: () =>
        apiClient.get(apiClient.addUrlParams('/api/google/test')),

    getAccounts: () =>
        apiClient.get(apiClient.addUrlParams('/api/google/accounts')),

    getLocations: (accountName) => {
        // Extract account ID from full account name (e.g., "accounts/123456" -> "123456")
        const accountId = accountName.startsWith('accounts/') 
            ? accountName.split('/')[1] 
            : accountName;
        return apiClient.get(apiClient.addUrlParams(`/api/google/accounts/${accountId}/locations`));
    },

    getLocationReviews: (accountName, locationName, pageSize = 50, pageToken = null) => {
        // Extract IDs from full names
        const accountId = accountName.startsWith('accounts/') 
            ? accountName.split('/')[1] 
            : accountName;
        const locationId = locationName.startsWith('locations/') 
            ? locationName.split('/')[1] 
            : locationName;
        
        const params = new URLSearchParams({ pageSize: pageSize.toString() });
        if (pageToken) params.append('pageToken', pageToken);
        
        const queryString = params.toString();
        const url = `/api/google/accounts/${accountId}/locations/${locationId}/reviews${queryString ? `?${queryString}` : ''}`;
        
        return apiClient.get(apiClient.addUrlParams(url));
    },

    syncReviews: (accountName, maxReviews = 1000, selectedLocations = null) => {
        // Extract account ID from full account name (e.g., "accounts/123456" -> "123456")
        const accountId = accountName.startsWith('accounts/') 
            ? accountName.split('/')[1] 
            : accountName;
        const body = { maxReviews };
        if (selectedLocations && selectedLocations.length > 0) {
            body.locations = selectedLocations;
        }
        return apiClient.post(apiClient.addUrlParams(`/api/google/accounts/${encodeURIComponent(accountId)}/sync`), body);
    },

    getLocalReviews: (params = {}) => {
        const searchParams = new URLSearchParams(params);
        const queryString = searchParams.toString();
        const url = `/api/google/reviews${queryString ? `?${queryString}` : ''}`;
        return apiClient.get(apiClient.addUrlParams(url));
    },

    assignReview: (reviewId, technicianIdOrCrmCode) => {
        // Always treat as crmCode since that's what we're passing from the technician selection
        const body = { crmCode: technicianIdOrCrmCode };
        return apiClient.post(apiClient.addUrlParams(`/api/google/reviews/${reviewId}/assign`), body);
    },

    generateResponse: (reviewId) =>
        apiClient.post(apiClient.addUrlParams(`/api/google/reviews/${reviewId}/generate-response`)),

    getReview: (reviewId) =>
        apiClient.get(apiClient.addUrlParams(`/api/google/reviews/${reviewId}`)),

    getSyncedLocations: () =>
        apiClient.get(apiClient.addUrlParams('/api/google/synced-locations')),

    flagReview: (reviewId, reason) =>
        apiClient.post(apiClient.addUrlParams(`/api/google/reviews/${reviewId}/flag`), { reason }),

    unflagReview: (reviewId) =>
        apiClient.post(apiClient.addUrlParams(`/api/google/reviews/${reviewId}/unflag`)),

    getPerformanceMetrics: (timeRange = '30', userId = null) => {
        let url = `/api/admin/performance-metrics?timeRange=${timeRange}`;
        if (userId) {
            url += `&userId=${userId}`;
        }
        return apiClient.get(apiClient.addUrlParams(url));
    },

    getManagers: () =>
        apiClient.get(apiClient.addUrlParams('/api/admin/managers')),

    getTechnicianPerformanceMetrics: (timeRange = '30', technicianId = null) => {
        let url = `/api/technician/performance-metrics?timeRange=${timeRange}`;
        if (technicianId) {
            url += `&technicianId=${technicianId}`;
        }
        return apiClient.get(apiClient.addUrlParams(url));
    },

    getTechnicians: () =>
        apiClient.get(apiClient.addUrlParams('/api/technicians/list'))
};

// Utility functions
export const utils = {
    getUrlParams: () => {
        const params = new URLSearchParams(window.location.search);
        return {
            location: params.get('location'),
            user: params.get('user')
        };
    }
};

// Export the main client for custom requests
export default apiClient;
